import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Common
    'app.name': 'LuxeCuts',
    'app.tagline': 'Premium Salon & Staff Management',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.loading': 'Loading...',
    'common.noData': 'No data found',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.time': 'Time',
    'common.price': 'Price',
    'common.total': 'Total',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.address': 'Address',
    'common.notes': 'Notes',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.all': 'All',
    'common.today': 'Today',
    'common.week': 'Week',
    'common.month': 'Month',
    'common.year': 'Year',
    
    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.logout': 'Logout',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.signUp': 'Sign Up',
    'auth.signIn': 'Sign In',
    'auth.googleLogin': 'Continue with Google',
    'auth.appleLogin': 'Continue with Apple',
    'auth.orContinueWith': 'or continue with',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.appointments': 'Appointments',
    'nav.calendar': 'Calendar',
    'nav.clients': 'Clients',
    'nav.services': 'Services',
    'nav.staff': 'Staff',
    'nav.barbers': 'Staff',
    'nav.pos': 'POS',
    'nav.sales': 'Sales',
    'nav.inventory': 'Inventory',
    'nav.products': 'Products',
    'nav.payouts': 'Payouts',
    'nav.expenses': 'Expenses',
    'nav.reports': 'Reports',
    'nav.leads': 'Leads',
    'nav.adminUsers': 'Users',
    'nav.adminCompanies': 'Companies',
    'nav.settings': 'Settings',
    'nav.company': 'Company',
    'nav.branches': 'Branches',
    'nav.promotions': 'Promotions',
    'nav.booking': 'Online Booking',
    'nav.salesHistory': 'Sales History',
    'nav.adminWhatsappLog': 'WhatsApp log',

    // Dashboard
    'dashboard.welcome': 'Welcome back',
    'dashboard.todayAppointments': "Today's Appointments",
    'dashboard.pendingArrivals': 'Pending Arrivals',
    'dashboard.completedToday': 'Completed Today',
    'dashboard.todayRevenue': "Today's Revenue",
    'dashboard.weekRevenue': 'Weekly Revenue',
    'dashboard.monthRevenue': 'Monthly Revenue',
    'dashboard.topBarber': 'Top Employee',
    'dashboard.lowStockAlerts': 'Low Stock Alerts',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.cancellations': 'Cancellations',
    'dashboard.noShows': 'No Shows',
    'dashboard.avgTicket': 'Avg. Ticket',
    'dashboard.totalClients': 'Total Clients',
    'dashboard.pendingPayouts': 'Pending Payouts',
    
    // Appointments
    'appointments.new': 'New Appointment',
    'appointments.walkin': 'Walk-in',
    'appointments.selectBranch': 'Select Branch',
    'appointments.selectBarber': 'Select Employee',
    'appointments.selectService': 'Select Service',
    'appointments.selectDate': 'Select Date',
    'appointments.selectTime': 'Select Time',
    'appointments.clientInfo': 'Client Information',
    'appointments.status.pending': 'Pending',
    'appointments.status.confirmed': 'Confirmed',
    'appointments.status.checkedIn': 'Checked In',
    'appointments.status.inProgress': 'In Progress',
    'appointments.status.completed': 'Completed',
    'appointments.status.cancelled': 'Cancelled',
    'appointments.status.noShow': 'No Show',
    
    // Clients
    'clients.new': 'New Client',
    'clients.search': 'Search clients...',
    'clients.visitCount': 'Visits',
    'clients.totalSpent': 'Total Spent',
    'clients.lastVisit': 'Last Visit',
    'clients.preferredBarber': 'Preferred Employee',
    'clients.history': 'History',
    
    // Services
    'services.new': 'New Service',
    'services.duration': 'Duration',
    'services.minutes': 'minutes',
    'services.category': 'Category',
    'services.promoPrice': 'Promo Price',
    
    // Barbers
    'barbers.new': 'New Employee',
    'barbers.specialties': 'Specialties',
    'barbers.paymentModel': 'Payment Model',
    'barbers.percentage': 'Percentage',
    'barbers.fixed': 'Fixed Fee',
    'barbers.commission': 'Commission Rate',
    'barbers.fixedAmount': 'Fixed Amount',
    'barbers.earnings': 'Earnings',
    'barbers.rating': 'Rating',
    
    // POS
    'pos.checkout': 'Checkout',
    'pos.addItem': 'Add Item',
    'pos.cart': 'Cart',
    'pos.subtotal': 'Subtotal',
    'pos.discount': 'Discount',
    'pos.tip': 'Tip',
    'pos.tax': 'Tax',
    'pos.paymentMethod': 'Payment Method',
    'pos.cash': 'Cash',
    'pos.card': 'Card',
    'pos.transfer': 'Bank Transfer',
    'pos.completeSale': 'Complete Sale',
    'pos.receipt': 'Receipt',
    'pos.sendReceipt': 'Send Receipt',
    'pos.printReceipt': 'Print Receipt',
    
    // Payouts
    'payouts.new': 'New Payout',
    'payouts.markPaid': 'Mark as Paid',
    'payouts.pending': 'Pending',
    'payouts.paid': 'Paid',
    'payouts.period': 'Period',
    'payouts.amount': 'Amount',
    
    // Inventory
    'inventory.low': 'Low Stock',
    'inventory.restock': 'Restock',
    'inventory.sku': 'SKU',
    'inventory.stock': 'Stock',
    'inventory.reorderThreshold': 'Reorder At',
    
    // Reports
    'reports.revenue': 'Revenue',
    'reports.services': 'Services',
    'reports.barbers': 'Employee Performance',
    'reports.export': 'Export',
    
    // Settings
    'settings.company': 'Company Settings',
    'settings.branches': 'Branch Settings',
    'settings.workingHours': 'Working Hours',
    'settings.holidays': 'Holidays',
    'settings.language': 'Language',
    'settings.timezone': 'Timezone',
    'settings.currency': 'Currency',
    'settings.payoutPolicy': 'Política de Pagos'
    , 'settings.cashiers': 'Usuarios Cajeros'
    , 'cashiers.new': 'Nuevo Cajero'
    , 'cashiers.invite': 'Invitar por correo'
    , 'cashiers.role': 'Rol'
    , 'cashiers.created': 'Cajero creado exitosamente'
    , 'appointments.cancel': 'Cancelar Cita'
    , 'appointments.reschedule': 'Reprogramar'
    , 'appointments.cancelConfirm': '¿Estás seguro de que deseas cancelar esta cita?'
    , 'appointments.newDate': 'Nueva Fecha'
    , 'appointments.newTime': 'Nueva Hora'
    , 'settings.cashiers': 'Cashier Users'
    , 'cashiers.new': 'New Cashier'
    , 'cashiers.invite': 'Invite by email'
    , 'cashiers.role': 'Role'
    , 'cashiers.created': 'Cashier created successfully'
    , 'appointments.cancel': 'Cancel Appointment'
    , 'appointments.reschedule': 'Reschedule'
    , 'appointments.cancelConfirm': 'Are you sure you want to cancel this appointment?'
    , 'appointments.newDate': 'New Date'
    , 'appointments.newTime': 'New Time',
    
    // Booking
    'booking.title': 'Book Your Appointment',
    'booking.selectServices': 'Select Services',
    'booking.yourDetails': 'Your Details',
    'booking.confirm': 'Confirm Booking',
    'booking.success': 'Booking Confirmed!',
    'booking.successMessage': 'Your appointment has been booked successfully.',

    // WhatsApp log
    'whatsappLog.testTitle': 'Send test message',
    'whatsappLog.testButton': 'Send test',
    'whatsappLog.testSent': 'Test message sent',
    'whatsappLog.testFailed': 'Test failed',
    'whatsappLog.testCooldown': 'Wait {seconds}s before sending again',
    'whatsappLog.recentTitle': 'Recent activity',
    'whatsappLog.empty': 'No messages sent yet.',
    'whatsappLog.refresh': 'Refresh',
    'whatsappLog.kind.confirmation': 'Confirmation',
    'whatsappLog.kind.reminder': 'Reminder',
    'whatsappLog.kind.test': 'Test',
    'notificationChannel.label': 'Notify via',
    'notificationChannel.whatsapp': 'WhatsApp',
    'notificationChannel.email': 'Email',
    'notificationChannel.none': 'No notification',
    'whatsappLog.kind.reschedule': 'Rescheduled',
    'whatsappLog.kind.cancel': 'Cancelled',
    'templates.reschedule.title': 'Reschedule',
    'templates.cancel.title': 'Cancel',
    'templates.fields.rescheduleEmailSubject': 'Reschedule email subject',
    'templates.fields.rescheduleEmailBody': 'Reschedule email body',
    'templates.fields.rescheduleWhatsapp': 'Reschedule WhatsApp message',
    'templates.fields.cancelEmailSubject': 'Cancel email subject',
    'templates.fields.cancelEmailBody': 'Cancel email body',
    'templates.fields.cancelWhatsapp': 'Cancel WhatsApp message',
    'whatsappLog.status.success': 'Success',
    'whatsappLog.status.failed': 'Failed',
    'whatsappLog.adminTitle': 'WhatsApp message log',
    'whatsappLog.adminNav': 'WhatsApp log',
    'whatsappLog.selectCompany': 'Select a company',
    'whatsappLog.filterKind': 'Type',
    'whatsappLog.filterStatus': 'Status',
    'whatsappLog.filterFrom': 'From',
    'whatsappLog.filterTo': 'To',
    'whatsappLog.filterClear': 'Clear filters',
    'whatsappLog.colTime': 'Time',
    'whatsappLog.colKind': 'Type',
    'whatsappLog.colStatus': 'Status',
    'whatsappLog.colPhone': 'Phone',
    'whatsappLog.colAppointment': 'Appointment',
    'whatsappLog.colMessage': 'Message',
    'whatsappLog.colError': 'Error',
    'whatsappLog.emptyAdmin': 'Select a company to view its log.',
    'whatsappLog.emptyRange': 'No messages in this range.',
    'whatsappLog.loadMore': 'Load more',
    'whatsappLog.all': 'All',
  },
  es: {
    // Common
    'app.name': 'LuxeCuts',
    'app.tagline': 'Gestión Premium de Personal',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Agregar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.loading': 'Cargando...',
    'common.noData': 'No hay datos',
    'common.actions': 'Acciones',
    'common.status': 'Estado',
    'common.date': 'Fecha',
    'common.time': 'Hora',
    'common.price': 'Precio',
    'common.total': 'Total',
    'common.name': 'Nombre',
    'common.email': 'Correo',
    'common.phone': 'Teléfono',
    'common.address': 'Dirección',
    'common.notes': 'Notas',
    'common.active': 'Activo',
    'common.inactive': 'Inactivo',
    'common.all': 'Todos',
    'common.today': 'Hoy',
    'common.week': 'Semana',
    'common.month': 'Mes',
    'common.year': 'Año',
    
    // Auth
    'auth.login': 'Iniciar Sesión',
    'auth.register': 'Registrarse',
    'auth.logout': 'Cerrar Sesión',
    'auth.email': 'Correo Electrónico',
    'auth.password': 'Contraseña',
    'auth.confirmPassword': 'Confirmar Contraseña',
    'auth.forgotPassword': '¿Olvidaste tu contraseña?',
    'auth.noAccount': '¿No tienes cuenta?',
    'auth.hasAccount': '¿Ya tienes cuenta?',
    'auth.signUp': 'Crear Cuenta',
    'auth.signIn': 'Ingresar',
    'auth.googleLogin': 'Continuar con Google',
    'auth.appleLogin': 'Continuar con Apple',
    'auth.orContinueWith': 'o continúa con',
    
    // Navigation
    'nav.dashboard': 'Panel',
    'nav.appointments': 'Citas',
    'nav.calendar': 'Calendario',
    'nav.clients': 'Clientes',
    'nav.services': 'Servicios',
    'nav.staff': 'Personal',
    'nav.barbers': 'Empleados',
    'nav.pos': 'POS',
    'nav.sales': 'Ventas',
    'nav.inventory': 'Inventario',
    'nav.products': 'Productos',
    'nav.payouts': 'Pagos',
    'nav.expenses': 'Gastos',
    'nav.reports': 'Reportes',
    'nav.leads': 'Leads',
    'nav.adminUsers': 'Usuarios',
    'nav.adminCompanies': 'Empresas',
    'nav.settings': 'Configuración',
    'nav.company': 'Empresa',
    'nav.branches': 'Sucursales',
    'nav.promotions': 'Promociones',
    'nav.booking': 'Reservas Online',
    'nav.salesHistory': 'Historial de Ventas',
    'nav.adminWhatsappLog': 'Log WhatsApp',

    // Dashboard
    'dashboard.welcome': 'Bienvenido de nuevo',
    'dashboard.todayAppointments': 'Citas de Hoy',
    'dashboard.pendingArrivals': 'Por Llegar',
    'dashboard.completedToday': 'Completadas Hoy',
    'dashboard.todayRevenue': 'Ingresos de Hoy',
    'dashboard.weekRevenue': 'Ingresos Semanales',
    'dashboard.monthRevenue': 'Ingresos Mensuales',
    'dashboard.topBarber': 'Mejor Empleado',
    'dashboard.lowStockAlerts': 'Alertas de Stock',
    'dashboard.recentActivity': 'Actividad Reciente',
    'dashboard.quickActions': 'Acciones Rápidas',
    'dashboard.cancellations': 'Cancelaciones',
    'dashboard.noShows': 'No Asistieron',
    'dashboard.avgTicket': 'Ticket Promedio',
    'dashboard.totalClients': 'Total Clientes',
    'dashboard.pendingPayouts': 'Pagos Pendientes',
    
    // Appointments
    'appointments.new': 'Nueva Cita',
    'appointments.walkin': 'Sin Cita',
    'appointments.selectBranch': 'Seleccionar Sucursal',
    'appointments.selectBarber': 'Seleccionar Empleado',
    'appointments.selectService': 'Seleccionar Servicio',
    'appointments.selectDate': 'Seleccionar Fecha',
    'appointments.selectTime': 'Seleccionar Hora',
    'appointments.clientInfo': 'Información del Cliente',
    'appointments.status.pending': 'Pendiente',
    'appointments.status.confirmed': 'Confirmada',
    'appointments.status.checkedIn': 'Registrado',
    'appointments.status.inProgress': 'En Progreso',
    'appointments.status.completed': 'Completada',
    'appointments.status.cancelled': 'Cancelada',
    'appointments.status.noShow': 'No Asistió',
    
    // Clients
    'clients.new': 'Nuevo Cliente',
    'clients.search': 'Buscar clientes...',
    'clients.visitCount': 'Visitas',
    'clients.totalSpent': 'Total Gastado',
    'clients.lastVisit': 'Última Visita',
    'clients.preferredBarber': 'Empleado Preferido',
    'clients.history': 'Historial',
    
    // Services
    'services.new': 'Nuevo Servicio',
    'services.duration': 'Duración',
    'services.minutes': 'minutos',
    'services.category': 'Categoría',
    'services.promoPrice': 'Precio Promocional',
    
    // Barbers
    'barbers.new': 'Nuevo Empleado',
    'barbers.specialties': 'Especialidades',
    'barbers.paymentModel': 'Modelo de Pago',
    'barbers.percentage': 'Porcentaje',
    'barbers.fixed': 'Monto Fijo',
    'barbers.commission': 'Tasa de Comisión',
    'barbers.fixedAmount': 'Monto Fijo',
    'barbers.earnings': 'Ganancias',
    'barbers.rating': 'Calificación',
    
    // POS
    'pos.checkout': 'Cobrar',
    'pos.addItem': 'Agregar',
    'pos.cart': 'Carrito',
    'pos.subtotal': 'Subtotal',
    'pos.discount': 'Descuento',
    'pos.tip': 'Propina',
    'pos.tax': 'Impuesto',
    'pos.paymentMethod': 'Método de Pago',
    'pos.cash': 'Efectivo',
    'pos.card': 'Tarjeta',
    'pos.transfer': 'Transferencia',
    'pos.completeSale': 'Completar Venta',
    'pos.receipt': 'Recibo',
    'pos.sendReceipt': 'Enviar Recibo',
    'pos.printReceipt': 'Imprimir Recibo',
    
    // Payouts
    'payouts.new': 'Nuevo Pago',
    'payouts.markPaid': 'Marcar como Pagado',
    'payouts.pending': 'Pendiente',
    'payouts.paid': 'Pagado',
    'payouts.period': 'Período',
    'payouts.amount': 'Monto',
    
    // Inventory
    'inventory.low': 'Stock Bajo',
    'inventory.restock': 'Reabastecer',
    'inventory.sku': 'SKU',
    'inventory.stock': 'Existencia',
    'inventory.reorderThreshold': 'Reordenar En',
    
    // Reports
    'reports.revenue': 'Ingresos',
    'reports.services': 'Servicios',
    'reports.barbers': 'Rendimiento de Empleados',
    'reports.export': 'Exportar',
    
    // Settings
    'settings.company': 'Configuración de Empresa',
    'settings.branches': 'Configuración de Sucursales',
    'settings.workingHours': 'Horarios de Trabajo',
    'settings.holidays': 'Días Festivos',
    'settings.language': 'Idioma',
    'settings.timezone': 'Zona Horaria',
    'settings.currency': 'Moneda',
    'settings.payoutPolicy': 'Política de Pagos',
    
    // Booking
    'booking.title': 'Reserva tu Cita',
    'booking.selectServices': 'Selecciona Servicios',
    'booking.yourDetails': 'Tus Datos',
    'booking.confirm': 'Confirmar Reserva',
    'booking.success': '¡Reserva Confirmada!',
    'booking.successMessage': 'Tu cita ha sido reservada exitosamente.',

    // WhatsApp log
    'whatsappLog.testTitle': 'Enviar mensaje de prueba',
    'whatsappLog.testButton': 'Enviar prueba',
    'whatsappLog.testSent': 'Mensaje de prueba enviado',
    'whatsappLog.testFailed': 'Falló la prueba',
    'whatsappLog.testCooldown': 'Espera {seconds}s antes de enviar de nuevo',
    'whatsappLog.recentTitle': 'Actividad reciente',
    'whatsappLog.empty': 'Aún no se han enviado mensajes.',
    'whatsappLog.refresh': 'Refrescar',
    'whatsappLog.kind.confirmation': 'Confirmación',
    'whatsappLog.kind.reminder': 'Recordatorio',
    'whatsappLog.kind.test': 'Prueba',
    'notificationChannel.label': 'Notificar por',
    'notificationChannel.whatsapp': 'WhatsApp',
    'notificationChannel.email': 'Email',
    'notificationChannel.none': 'Sin notificación',
    'whatsappLog.kind.reschedule': 'Reagendado',
    'whatsappLog.kind.cancel': 'Cancelado',
    'templates.reschedule.title': 'Reagendado',
    'templates.cancel.title': 'Cancelado',
    'templates.fields.rescheduleEmailSubject': 'Asunto email de reagendado',
    'templates.fields.rescheduleEmailBody': 'Cuerpo email de reagendado',
    'templates.fields.rescheduleWhatsapp': 'Mensaje WhatsApp de reagendado',
    'templates.fields.cancelEmailSubject': 'Asunto email de cancelación',
    'templates.fields.cancelEmailBody': 'Cuerpo email de cancelación',
    'templates.fields.cancelWhatsapp': 'Mensaje WhatsApp de cancelación',
    'whatsappLog.status.success': 'Éxito',
    'whatsappLog.status.failed': 'Fallo',
    'whatsappLog.adminTitle': 'Log de mensajes de WhatsApp',
    'whatsappLog.adminNav': 'Log WhatsApp',
    'whatsappLog.selectCompany': 'Selecciona una empresa',
    'whatsappLog.filterKind': 'Tipo',
    'whatsappLog.filterStatus': 'Estado',
    'whatsappLog.filterFrom': 'Desde',
    'whatsappLog.filterTo': 'Hasta',
    'whatsappLog.filterClear': 'Limpiar filtros',
    'whatsappLog.colTime': 'Hora',
    'whatsappLog.colKind': 'Tipo',
    'whatsappLog.colStatus': 'Estado',
    'whatsappLog.colPhone': 'Teléfono',
    'whatsappLog.colAppointment': 'Cita',
    'whatsappLog.colMessage': 'Mensaje',
    'whatsappLog.colError': 'Error',
    'whatsappLog.emptyAdmin': 'Selecciona una empresa para ver el log.',
    'whatsappLog.emptyRange': 'No hay mensajes en este rango.',
    'whatsappLog.loadMore': 'Cargar más',
    'whatsappLog.all': 'Todos',
  },
};

const I18nContext = createContext(null);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('locale');
      if (stored) return stored;
      const browserLang = navigator.language.split('-')[0];
      return ['es', 'en'].includes(browserLang) ? browserLang : 'en';
    }
    return 'en';
  });

  const t = useCallback((key, params = {}) => {
    let text = translations[locale]?.[key] || translations['en']?.[key] || key;
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
    return text;
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    if (['es', 'en'].includes(newLocale)) {
      setLocale(newLocale);
      localStorage.setItem('locale', newLocale);
    }
  }, []);

  const value = {
    locale,
    setLocale: changeLocale,
    t,
    isEnglish: locale === 'en',
    isSpanish: locale === 'es',
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export default I18nContext;
